import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import "./AIAlerts.scss";
import TradeAlgoTable from "../common/Tables/TradeAlgoTable";
import { getAIPastAlertsColumns } from "./aiAlertsColumns";
import { Tooltip } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import {
  getSelectedPeriod,
  getFilteredAlerts,
  getIndicatorData,
  getPastAlertsLoading,
  pastAlertsLoad,
  pastAlertsSetPeriod,
} from "../../appRedux/ducks/pastAlerts";
import { showSnackbar } from "../../appRedux/ducks/snackbar";
import moment from "moment-timezone";
import BeautifulSkeleton from "../common/Skeletons/BeautifulSkeleton";

/**
 * Format last updated timestamp to "Last updated: Thursday, Nov 28, 2024 at 1:23 PM (EST)"
 * Uses EST timezone to match Figma design
 */
const formatLastUpdated = (timestamp) => {
  if (!timestamp) return "";
  const date = moment.tz(timestamp, "America/New_York");
  const weekdays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const weekday = weekdays[date.day()];
  const month = months[date.month()];
  const day = date.date();
  const year = date.year();
  const time = date.format("h:mm A");

  return `Last updated: ${weekday}, ${month} ${day}, ${year} at ${time} (EST)`;
};

const PastAlerts = ({
  themeMode,
  onShowDetails,
  onLatestActivityDateChange,
}) => {
  const dispatch = useDispatch();
  // Store sort models per date group to scope sorting to individual tables
  const [sortModels, setSortModels] = useState({});

  // Redux selectors
  const selectedPeriod = useSelector(getSelectedPeriod);
  const filteredAlerts = useSelector(getFilteredAlerts);
  const indicatorData = useSelector(getIndicatorData);
  const loading = useSelector(getPastAlertsLoading);

  // Load past alerts on mount and when period changes
  useEffect(() => {
    dispatch(pastAlertsLoad());
  }, [dispatch, selectedPeriod]);

  // Period options
  const periodOptions = [
    { value: "all", label: "All" },
    { value: "1M", label: "1M" },
    { value: "3M", label: "3M" },
    { value: "6M", label: "6M" },
    { value: "YTD", label: "YTD" },
    { value: "1Y", label: "1Y" },
  ];

  const handlePeriodChange = (period) => {
    dispatch(pastAlertsSetPeriod(period));
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        dispatch(showSnackbar("Copied to clipboard", { variant: "success" }));
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
        dispatch(
          showSnackbar("Failed to copy to clipboard", { variant: "error" }),
        );
      });
  };

  const handleSeeDetails = (row) => {
    if (onShowDetails) {
      onShowDetails(row.id);
    }
  };

  // Handler to update sort model for a specific date group
  const handleSortModelChange = (dateGroup, newSortModel) => {
    setSortModels((prev) => ({
      ...prev,
      [dateGroup]: newSortModel,
    }));
  };

  // Get shared column definitions
  const columns = getAIPastAlertsColumns(
    handleCopyToClipboard,
    handleSeeDetails,
  );

  // Row className function to highlight positive P&L rows
  const getRowClassName = (params) => {
    const plValue = params.row.pl;
    if (plValue !== null && plValue !== undefined && plValue >= 0) {
      return "ai-alerts-row-positive-pl";
    }
    return "";
  };

  // Cutoff date: November 6, 2025 (pre-launch trades cutoff)
  const PRE_LAUNCH_CUTOFF_DATE = moment.utc("2025-11-06").endOf("day");

  // Separate alerts into pre-launch (before Nov 6) and forward tested (after Nov 6) groups
  // Then group each by date for rendering
  const { preLaunchGroups, forwardTestedGroups } = useMemo(() => {
    const preLaunchGroups = {};
    const forwardTestedGroups = {};

    filteredAlerts.forEach((alert) => {
      // Use the original alert data to get the actual dates
      const originalAlert = alert._original;
      // For "completed" trades, use aiExitDate if available (when trade was completed)
      // Otherwise fall back to aiBuyDate (when alert was sent)
      // The original API dates are strings in format "YYYY-MM-DD"
      let tradeDate = originalAlert?.aiExitDate || originalAlert?.aiBuyDate;

      // If no date from original, try using the transformed alert.date (Date object)
      if (!tradeDate && alert.date) {
        tradeDate = moment.utc(alert.date).format("YYYY-MM-DD");
      }

      if (!tradeDate) {
        // If no date available, skip this alert or log a warning
        return;
      }

      // Parse the date string and compare (both dates are UTC)
      const alertDate = moment.utc(tradeDate).startOf("day");
      const isPreLaunch = alertDate.isBefore(PRE_LAUNCH_CUTOFF_DATE);

      // Group by dateGroup within the appropriate category
      const targetGroups = isPreLaunch ? preLaunchGroups : forwardTestedGroups;

      if (!targetGroups[alert.dateGroup]) {
        targetGroups[alert.dateGroup] = [];
      }
      targetGroups[alert.dateGroup].push(alert);
    });

    return { preLaunchGroups, forwardTestedGroups };
  }, [filteredAlerts]);

  // Calculate latest activity date from all alerts
  // Uses the NEWEST date value available (aiBuyDate or aiExitDate, whichever is newer)
  const latestActivityDate = useMemo(() => {
    const allDates = [];
    filteredAlerts.forEach((alert) => {
      // Use the original alert data from the API
      const originalAlert = alert._original;
      if (originalAlert) {
        // Consider both aiBuyDate and aiExitDate, use whichever is newer
        const dates = [];
        if (originalAlert.aiBuyDate) {
          dates.push(moment.utc(originalAlert.aiBuyDate));
        }
        if (originalAlert.aiExitDate) {
          dates.push(moment.utc(originalAlert.aiExitDate));
        }
        if (originalAlert.lastUpdatedTimestamp && dates.length === 0) {
          dates.push(moment.utc(originalAlert.lastUpdatedTimestamp));
        }

        // Get the newest date for this alert
        if (dates.length > 0) {
          const newestDateForAlert = moment.max(dates);
          allDates.push(newestDateForAlert);
        }
      }
    });

    if (allDates.length === 0) {
      // If no data, return current time in EST
      return formatLastUpdated(moment.tz("America/New_York").toISOString());
    }

    // Find the latest date across all alerts
    const latestDate = moment.max(allDates);
    return formatLastUpdated(latestDate.toISOString());
  }, [filteredAlerts]);

  // Notify parent component of latest activity date change
  useEffect(() => {
    if (onLatestActivityDateChange) {
      onLatestActivityDateChange(latestActivityDate);
    }
  }, [latestActivityDate, onLatestActivityDateChange]);

  return (
    <div className="past-alerts-content">
      {/* Filter Section - Period Selector + Indicator */}
      <div className="past-alerts-filter-section">
        {/* Period Selector */}
        <div className="past-alerts-period-selector">
          {periodOptions.map((option) => (
            <button
              key={option.value}
              className={`past-alerts-period-button ${
                selectedPeriod === option.value ? "selected" : ""
              }`}
              onClick={() => handlePeriodChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Indicator */}
        <div className="past-alerts-indicator">
          <span>SINCE {indicatorData.sinceDate}</span>
          <span>·</span>
          <span>{indicatorData.tradeCount} TRADES</span>
        </div>
      </div>

      {/* Stats Section */}
      <div className="past-alerts-stats-section">
        {/* Win Rate Card */}
        <div className="past-alerts-stat-card">
          <div className="stat-card-label">
            <span className="stat-label-text">Win Rate</span>
            <span className="stat-label-period">HOURLY</span>
          </div>
          <p className="stat-card-value stat-value-positive">
            {indicatorData.winRate || "66.67%"}
          </p>
        </div>

        {/* Overall P&L Card */}
        <div className="past-alerts-stat-card">
          <div className="stat-card-label">
            <span className="stat-label-text">Overall P&L</span>
            <Tooltip
              arrow
              placement="top"
              title={
                <div className="custom-AI-tooltip">
                  <div className="custom-AI-tooltip-content">
                    Overall P&L shows our real-time internally calculated P&L
                    from our paper portfolio driven by the AI trade alerts.
                    Actual entry and exit prices are market-dependent.
                  </div>
                </div>
              }
            >
              <InfoOutlinedIcon className="stat-info-icon" />
            </Tooltip>
          </div>
          <p className="stat-card-value stat-value-positive">
            {indicatorData.overallPnL || "+105.25%"}
          </p>
        </div>
      </div>

      {/* Alerts Table */}
      <div className="ai-alerts-table-container">
        {/* Show loading skeleton when loading and no data yet */}
        {loading &&
        Object.keys(forwardTestedGroups).length === 0 &&
        Object.keys(preLaunchGroups).length === 0 ? (
          <>
            {/* First date group skeleton */}
            <div className="ai-alerts-date-group-section">
              <div className="ai-alerts-date-group">
                <BeautifulSkeleton width={250} height={16} />
              </div>
              <TradeAlgoTable
                rows={[]}
                columns={columns}
                loading={loading}
                sortModel={[]}
                setSortModel={() => {}}
                tableClassName="ai-alerts-table"
                hasRowClassName={true}
                rowClassName={getRowClassName}
                customToolbar={false}
                threshold={0}
              />
            </div>
            {/* Second date group skeleton */}
            <div className="ai-alerts-date-group-section">
              <div className="ai-alerts-date-group">
                <BeautifulSkeleton
                  width={200}
                  height={16}
                  style={{ margin: "0 auto" }}
                />
              </div>
              <TradeAlgoTable
                rows={[]}
                columns={columns}
                loading={loading}
                sortModel={[]}
                setSortModel={() => {}}
                tableClassName="ai-alerts-table"
                hasRowClassName={true}
                rowClassName={getRowClassName}
                customToolbar={false}
                threshold={0}
              />
            </div>
          </>
        ) : (
          <>
            {/* Forward Tested Trades Section (after Nov 6) */}
            {Object.keys(forwardTestedGroups).length > 0 && (
              <>
                {Object.entries(forwardTestedGroups).map(
                  ([dateGroup, alerts], index) => (
                    <div
                      key={`forward-${index}`}
                      className="ai-alerts-date-group-section"
                    >
                      <div className="ai-alerts-date-group">
                        <span className="date-group-text">{dateGroup}</span>
                      </div>
                      <TradeAlgoTable
                        rows={alerts}
                        columns={columns}
                        loading={loading}
                        sortModel={sortModels[dateGroup] || []}
                        setSortModel={(newSortModel) =>
                          handleSortModelChange(dateGroup, newSortModel)
                        }
                        tableClassName="ai-alerts-table"
                        hasRowClassName={true}
                        rowClassName={getRowClassName}
                        customToolbar={false}
                        threshold={0}
                      />
                    </div>
                  ),
                )}
              </>
            )}

            {/* Separator between Forward Tested and Pre-Launch */}
            {/* Show separator whenever there are pre-launch trades */}
            {Object.keys(preLaunchGroups).length > 0 && (
              <div className="ai-alerts-separator">
                <span className="separator-text">
                  PRE-LAUNCH TRADES (BEFORE NOV 6)
                </span>
              </div>
            )}

            {/* Pre-Launch Trades Section (before Nov 6) */}
            {Object.keys(preLaunchGroups).length > 0 && (
              <>
                {Object.entries(preLaunchGroups).map(
                  ([dateGroup, alerts], index) => (
                    <div
                      key={`prelaunch-${index}`}
                      className="ai-alerts-date-group-section"
                    >
                      <div className="ai-alerts-date-group">
                        <span className="date-group-text">{dateGroup}</span>
                      </div>
                      <TradeAlgoTable
                        rows={alerts}
                        columns={columns}
                        loading={loading}
                        sortModel={sortModels[dateGroup] || []}
                        setSortModel={(newSortModel) =>
                          handleSortModelChange(dateGroup, newSortModel)
                        }
                        tableClassName="ai-alerts-table"
                        hasRowClassName={true}
                        rowClassName={getRowClassName}
                        customToolbar={false}
                        threshold={0}
                      />
                    </div>
                  ),
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PastAlerts;
